import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { agreementKeys, agreementTemplateKeys } from '@/lib/query-keys';
import { agreementService, agreementTemplateService } from '@/services/agreement.service';
import { useAgreementFilterStore } from '@/stores/agreementFilterStore';
import type { AgreementTemplate } from '@/types/table';

export function useAgreements() {
  const queryClient = useQueryClient();
  const filters = useAgreementFilterStore();

  const agreementsQuery = useQuery({
    queryKey: agreementKeys.list(filters.current),
    queryFn: () => agreementService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const invalidateAll = () => queryClient.invalidateQueries({ queryKey: agreementKeys.all });

  const createAgreement = useMutation({
    mutationFn: (data: Record<string, unknown>) => agreementService.create(data),
    onSuccess: () => { invalidateAll(); toast.success('Agreement created'); },
    onError: () => toast.error('Failed to create agreement'),
  });

  const updateAgreement = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => agreementService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(id) });
      toast.success('Agreement updated');
    },
    onError: () => toast.error('Failed to update agreement'),
  });

  const deleteAgreement = useMutation({
    mutationFn: (id: string) => agreementService.delete(id),
    onSuccess: () => { invalidateAll(); toast.success('Agreement deleted'); },
    onError: () => toast.error('Failed to delete agreement'),
  });

  const sendAgreement       = useMutation({ mutationFn: (id: string) => agreementService.send(id),          onSuccess: () => { invalidateAll(); toast.success('Agreement sent'); },         onError: () => toast.error('Failed to send agreement') });
  const markSigned          = useMutation({ mutationFn: (id: string) => agreementService.markSigned(id),    onSuccess: () => { invalidateAll(); toast.success('Marked as signed'); },        onError: () => toast.error('Failed to mark signed') });
  const markActive          = useMutation({ mutationFn: (id: string) => agreementService.markActive(id),    onSuccess: () => { invalidateAll(); toast.success('Marked as active'); },        onError: () => toast.error('Failed to mark active') });
  const markCompleted       = useMutation({ mutationFn: (id: string) => agreementService.markCompleted(id), onSuccess: () => { invalidateAll(); toast.success('Marked as completed'); },     onError: () => toast.error('Failed to mark completed') });
  const cancelAgreement     = useMutation({ mutationFn: (id: string) => agreementService.cancel(id),        onSuccess: () => { invalidateAll(); toast.success('Agreement cancelled'); },     onError: () => toast.error('Failed to cancel') });
  const duplicateAgreement  = useMutation({ mutationFn: (id: string) => agreementService.duplicate(id),     onSuccess: () => { invalidateAll(); toast.success('Agreement duplicated'); },    onError: () => toast.error('Failed to duplicate') });

  return {
    agreements: agreementsQuery.data?.data ?? [],
    meta: agreementsQuery.data?.meta,
    isLoading: agreementsQuery.isLoading,
    filters,
    createAgreement, updateAgreement, deleteAgreement,
    sendAgreement, markSigned, markActive, markCompleted, cancelAgreement, duplicateAgreement,
    downloadPdf: (id: string) => agreementService.downloadPdf(id),
  };
}

export function useAgreement(id: string) {
  const agreementQuery = useQuery({
    queryKey: agreementKeys.detail(id),
    queryFn: () => agreementService.getById(id),
    enabled: !!id,
  });
  return {
    agreement: agreementQuery.data?.data,
    isLoading: agreementQuery.isLoading,
  };
}

// ── Templates ─────────────────────────────────────────────────────────

export function useAgreementTemplates(opts?: { kind?: string; includeInactive?: boolean }) {
  const queryClient = useQueryClient();
  const params = { kind: opts?.kind, includeInactive: opts?.includeInactive };

  const templatesQuery = useQuery({
    queryKey: agreementTemplateKeys.list(params),
    queryFn: () => agreementTemplateService.list(params),
  });

  const createTemplate = useMutation({
    mutationFn: (data: Partial<AgreementTemplate>) => agreementTemplateService.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: agreementTemplateKeys.all }); toast.success('Template created'); },
    onError: () => toast.error('Failed to create template'),
  });

  const updateTemplate = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AgreementTemplate> }) => agreementTemplateService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: agreementTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: agreementTemplateKeys.detail(id) });
      toast.success('Template updated');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to update template';
      toast.error(msg);
    },
  });

  const removeTemplate = useMutation({
    mutationFn: (id: string) => agreementTemplateService.remove(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: agreementTemplateKeys.all }); toast.success('Template deleted'); },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to delete template';
      toast.error(msg);
    },
  });

  const cloneTemplate = useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => agreementTemplateService.clone(id, name),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: agreementTemplateKeys.all }); toast.success('Template cloned'); },
    onError: () => toast.error('Failed to clone template'),
  });

  return {
    templates: templatesQuery.data?.data ?? [],
    isLoading: templatesQuery.isLoading,
    createTemplate, updateTemplate, removeTemplate, cloneTemplate,
  };
}

export function useAgreementTemplate(id: string) {
  const templateQuery = useQuery({
    queryKey: agreementTemplateKeys.detail(id),
    queryFn: () => agreementTemplateService.get(id),
    enabled: !!id,
  });
  return {
    template: templateQuery.data?.data,
    isLoading: templateQuery.isLoading,
  };
}
